/**
 * Bloqueio por biometria (digital / Face ID).
 *
 * POR QUE EXISTE: a sessao agora sobrevive ao fechamento do app (ver
 * platform/storage.js). Isso e bom para a usuaria e ruim para a privacidade —
 * quem pegar o celular desbloqueado abre o Metria e ve faturamento, clientes e
 * custos. A biometria protege o que a persistencia expos.
 *
 * NAO substitui o login: o token continua sendo a credencial. Isto e uma
 * tranca local, opcional, que a usuaria liga se quiser.
 */

import { isNative, getPlugin } from './native';

const CHAVE_PREF = 'biometria_ativa';

/**
 * EXCECAO A REGRA DO PROJETO: os outros adaptadores acessam os plugins pelo
 * global window.Capacitor.Plugins, sem dependencia no frontend. Este plugin
 * nao permite: o lado nativo expoe apenas checkBiometry, e authenticate mora
 * na camada JS do pacote (verificado no @PluginMethod do fonte Android).
 *
 * Import DINAMICO para o custo ficar so no app: o webpack separa em chunk e o
 * bundle web nunca o carrega, porque isNative() e falso no navegador.
 */
let cache = null;

/**
 * Devolve { p } e nao o plugin direto DE PROPOSITO.
 *
 * BiometricAuth e um Proxy que encaminha qualquer acesso de propriedade para o
 * nativo. Retornado de uma funcao async, o JavaScript tenta assimila-lo como
 * promise e le `.then` — que o Proxy encaminha, resultando em
 * "BiometricAuthNative.then() is not implemented on android". Embrulhado num
 * objeto comum, nada disso acontece.
 */
async function api() {
  if (cache) return cache;
  if (!isNative() || !getPlugin('BiometricAuthNative')) return null;
  try {
    const m = await import('@aparajita/capacitor-biometric-auth');
    cache = { p: m.BiometricAuth };
    return cache;
  } catch {
    return null;
  }
}

export function disponivel() {
  return isNative() && !!getPlugin('BiometricAuthNative');
}

/** O aparelho tem biometria cadastrada e utilizavel? */
export async function suportada() {
  const api_ = await api();
  if (!api_) return false;
  try {
    const r = await api_.p.checkBiometry();
    return !!(r && r.isAvailable);
  } catch {
    return false;
  }
}

/** Preferencia da usuaria. Fica em localStorage: perder isso e inofensivo
 *  (volta a nao pedir biometria), diferente do token. */
export function ativa() {
  try {
    return localStorage.getItem(CHAVE_PREF) === 'true';
  } catch {
    return false;
  }
}

export function definirAtiva(valor) {
  try {
    localStorage.setItem(CHAVE_PREF, valor ? 'true' : 'false');
  } catch { /* storage cheio: segue sem persistir a preferencia */ }
}

/**
 * Pede a biometria.
 * @returns {Promise<{ ok: boolean, motivo?: string }>}
 *   motivo: 'indisponivel' | 'cancelado' | 'sem-biometria' | 'erro'
 */
export async function autenticar({ motivo = 'Confirme que é você para abrir o Metria' } = {}) {
  const api_ = await api();
  if (!api_) return { ok: false, motivo: 'indisponivel' };

  try {
    await api_.p.authenticate({
      reason: motivo,
      cancelTitle: 'Cancelar',
      allowDeviceCredential: true,   // deixa cair na senha do aparelho
      iosFallbackTitle: 'Usar senha do aparelho',
      androidTitle: 'Metria',
      androidSubtitle: motivo,
      androidConfirmationRequired: false,
    });
    return { ok: true };
  } catch (e) {
    const cod = (e && (e.code || e.message) ? String(e.code || e.message) : '').toLowerCase();
    if (cod.includes('cancel') || cod.includes('userfallback')) return { ok: false, motivo: 'cancelado' };
    if (cod.includes('notenrolled') || cod.includes('notavailable')) return { ok: false, motivo: 'sem-biometria' };
    return { ok: false, motivo: 'erro' };
  }
}
